import React from 'react';
import { cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from './Card';

/**
 * Metronic ActionCard - For quick actions and buttons
 * Perfect for dashboard quick actions, call-to-action sections
 *
 * @param {string} title - Card title
 * @param {string} description - Optional description
 * @param {React.ReactNode} children - Action buttons/content
 * @param {string} className - Additional classes
 */
const ActionCard = ({
  title,
  description,
  children,
  className
}) => {
  return (
    <Card className={cn("h-full", className)}>
      {(title || description) && (
        <CardHeader>
          {title && <CardTitle>{title}</CardTitle>}
          {description && (
            <p className="text-sm text-muted-foreground mt-1">
              {description}
            </p>
          )}
        </CardHeader>
      )}
      <CardContent className={cn(
        "space-y-3",
        !title && !description && "pt-6" // Add top padding if no header
      )}>
        {children}
      </CardContent>
    </Card>
  );
};

/**
 * ActionButton - Styled button for use inside ActionCard
 */
export const ActionButton = ({
  children,
  onClick,
  variant = 'primary',
  icon: Icon,
  disabled = false,
  className
}) => {
  const variants = {
    primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    success: 'bg-green-500 text-white hover:bg-green-600',
    danger: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
    outline: 'border border-border bg-background hover:bg-accent hover:text-accent-foreground',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "w-full flex items-center justify-center px-4 py-3 text-sm font-medium rounded-lg",
        "transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        variants[variant],
        className
      )}
    >
      {Icon && <Icon className="h-5 w-5 mr-2" />}
      {children}
    </button>
  );
};

export default ActionCard;

